import {
    type Employee,
    type RosterNoteDTO,
    VacationPolicy,
    dateAdd,
    formatDateShort,
    matchesFilters,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import "../../elements/popover";
import "../../elements/scroller";
import { app } from "../../init";
import { shared } from "../../styles";

@customElement("ptc-roster-note")
export class RosterNoteEl extends LitElement {
    @property({ attribute: false })
    rosterNote: RosterNoteDTO;

    @property({ attribute: false })
    employee: Employee;

    @property({ type: Boolean })
    collapsed = true;

    @property({ type: Number })
    maxHeight = 50;

    @query("pre")
    private _noteTextContainer: HTMLPreElement;

    @query(".box")
    private _box: HTMLDivElement;

    @query(".expand-button")
    private _expandButton: HTMLDivElement;

    private get _expandable() {
        return this._noteTextContainer && this._noteTextContainer.offsetHeight > this.maxHeight;
    }

    private _mutationObserver = new MutationObserver(() => this._updateDynamicStyles());
    private _intersectionObserver = new IntersectionObserver(() => this._updateDynamicStyles());

    private async _updateDynamicStyles() {
        if (!this._expandButton) {
            await this.updateComplete;
        }
        this._expandButton.style.display = this._expandable ? "" : "none";
        this._box.style.paddingBottom = this._expandable && !this.collapsed ? "2em" : "";
    }

    connectedCallback() {
        super.connectedCallback();
        this._mutationObserver.observe(this, { childList: true, subtree: true });
        this._intersectionObserver.observe(this);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._mutationObserver.disconnect();
        this._intersectionObserver.disconnect();
    }

    updated() {
        void this._updateDynamicStyles();
    }

    static styles = [
        shared,
        css`
            :host {
                display: block;
            }
        `,
    ];

    render() {
        const company = app.company;
        if (!company) {
            return;
        }
        const note = this.rosterNote;
        const noteEndForDisplay = dateAdd(note.end, { days: -1 });
        const departments = app.departments.filter((department) =>
            matchesFilters(note.filters, { company, department })
        );
        const ownDepartments = departments.filter((d) => this.employee.positions.some((p) => p.departmentId === d.id));
        return html`
            <div
                class="padded box relative"
                style="--color-highlight: ${note.color || "unset"}; transition: padding-bottom 0.2s;"
            >
                <div class="subtle spacing horizontal layout" style="margin-bottom: 0.25em">
                    <div class="stretch">
                        ${formatDateShort(note.start)}
                        ${noteEndForDisplay !== note.start ? `- ${formatDateShort(noteEndForDisplay)}` : ""}
                    </div>
                    <div class="smaller pills" style="margin-right: -0.5em; margin-top: -0.25em;">
                        ${ownDepartments.map((d) => html`<div class="pill">${d.name}</div>`)}
                        ${departments.length > ownDepartments.length
                            ? html`<div class="pill">+${departments.length - ownDepartments.length}</div>`
                            : ""}
                        ${note.vacationPolicy !== VacationPolicy.AllowAll
                            ? html`
                                  <div class="red pill strike-through">
                                      ${note.vacationPolicy === VacationPolicy.DisallowAll ? "Urlaub" : "Urlaubsantr."}
                                  </div>
                              `
                            : ""}
                    </div>
                </div>

                <ptc-ui-collapse .collapsedHeight=${this.maxHeight} .collapsed=${this.collapsed}>
                    <pre style="white-space: pre-wrap; line-height: 1.3em;">${note.text}</pre>
                </ptc-ui-collapse>

                <div class="bottom right expand-button absolute" style="background: rgba(255, 255, 255, 0.9);">
                    ${this.collapsed
                        ? html`<div class="padded link" @click=${() => (this.collapsed = false)}>
                              mehr <ptc-ui-icon name="icon.navigation.forward" class="smaller"></ptc-ui-icon>
                          </div>`
                        : html` <div class="padded link" @click=${() => (this.collapsed = true)}>
                              <ptc-ui-icon name="icon.navigation.back" class="smaller"></ptc-ui-icon> weniger
                          </div>`}
                </div>
            </div>
        `;
    }
}
